import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import * as d3 from "d3";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { globalVars } from "../../global/GlobalVars.js";
import DataAndColorPicker from "./DataAndColorPicker.jsx";
import * as render from "../../utils/render.js";
import { de, enUS, it } from "date-fns/locale";
import * as language from "../../utils/language.js";
import * as XLSX from "xlsx";

//========================================================================
// api-Funktionen
//========================================================================
// Konfiguration der api-Schnittstelle
const histDataUrl =
  window.location.port === "3000"
    ? `http://${window.location.hostname}:${globalVars.httpPort}/histData/`
    : `/api/histData/`;

//========================================================================
// Daten abrufen
const fetchData = async (ids, startDate, endDate, type, setDatasets) => {
  try {
    const responses = await Promise.all(
      ids.map((id) =>
        axios.get(histDataUrl + "data", {
          params: {
            startDateString: startDate.toISOString(),
            endDateString: endDate.toISOString(),
            id,
            type: type,
          },
        }),
      ),
    );

    // Datenpunkt mit endDate und value von letztem Datenpunkt hinzufügen
    const addLastDataPoint = (data, endDate) => {
      if (data.length === 0) {
        return data;
      } else {
        const lastDataPoint = data[data.length - 1];
        return [
          ...data,
          { value: lastDataPoint.value, timestamp: endDate.toISOString() },
        ];
      }
    };

    const allData = responses.map((response, index) => ({
      data: response.data.data,
      info: {
        id: response.data.id,
        name: response.data.name || [`Dataset ${index + 1}`],
        unit: response.data.unit || "",
        decimalPoints: response.data.decimalPoints || 0,
      },

      // Datenpunkt mit endDate und value von letztem Datenpunkt hinzufügen
      data: addLastDataPoint(response.data.data, endDate),
    }));

    setDatasets(allData);
  } catch (error) {
    console.error("Fehler beim Abrufen der Daten:", error);
  }
};

//========================================================================
// Liste aller aufgezeichneten Datenpunkte anfordern
const fetchHistoricPoints = async (setHistoricPoints) => {
  try {
    const response = await axios.get(histDataUrl + "historicPoints");
    setHistoricPoints(response.data);
  } catch (error) {
    console.error("Fehler beim Abrufen der Datenpunkte:", error);
  }
};

//========================================================================
// Charts speichern
const saveChart = async (
  id,
  type,
  selectedPoints,
  setSaveName,
  setSavedCharts,
) => {
  const chartData = {
    id: id,
    type: type,
    idChart1: selectedPoints[1].id,
    colorChart1: selectedPoints[1].color,
    idChart2: selectedPoints[2].id,
    colorChart2: selectedPoints[2].color,
    idChart3: selectedPoints[3].id,
    colorChart3: selectedPoints[3].color,
    idChart4: selectedPoints[4].id,
    colorChart4: selectedPoints[4].color,
    idChart5: selectedPoints[5].id,
    colorChart5: selectedPoints[5].color,
    idChart6: selectedPoints[6].id,
    colorChart6: selectedPoints[6].color,
    idChart7: selectedPoints[7].id,
    colorChart7: selectedPoints[7].color,
    idChart8: selectedPoints[8].id,
    colorChart8: selectedPoints[8].color,
    idChart9: selectedPoints[9].id,
    colorChart9: selectedPoints[9].color,
    idChart10: selectedPoints[10].id,
    colorChart10: selectedPoints[10].color,
  };

  try {
    await axios
      .post(histDataUrl + "saveCharts", {
        chartData,
      })
      .then(() => getSavedChartsList(setSavedCharts))
      .then(() => setSaveName(""));
  } catch (error) {
    console.error("Fehler beim Speichern des Charts:", error);
  }
};

//========================================================================
// Anforderung Liste der gespeicherten Charts
const getSavedChartsList = async (setSavedCharts) => {
  try {
    const response = await axios.get(histDataUrl + "savedChartsList");
    setSavedCharts(response.data);
  } catch (error) {
    console.error(
      "Fehler beim Abrufen der Liste der gespeicherten Charts:",
      error,
    );
  }
};

//========================================================================
// Anforderung gespeicherte Charts
const getSavedCharts = async (event, setSelectedPoints, setSelectedId) => {
  // Standardmäßig definierte Farben für jedes Chart
  const selectedPointsDefault = [
    { id: "", color: "" },
    { id: "", color: "#ED1212" },
    { id: "", color: "#36A2EB" },
    { id: "", color: "#FFCE56" },
    { id: "", color: "#4BC0C0" },
    { id: "", color: "#9966FF" },
    { id: "", color: "#ED1212" },
    { id: "", color: "#36A2EB" },
    { id: "", color: "#FFCE56" },
    { id: "", color: "#4BC0C0" },
    { id: "", color: "#9966FF" },
  ];

  // Setze initial die Default-Punkte
  setSelectedPoints(selectedPointsDefault);

  try {
    // Ausgewählten Wert speichern
    const selectedValue = event.target.value;
    setSelectedId(selectedValue);

    // Hole gespeicherte Charts vom Server
    const response = await axios.get(histDataUrl + "savedCharts", {
      params: { chartName: selectedValue },
    });

    // Wenn eine Farbe null (oder undefined) ist, greift die Default-Farbe
    const selectedPoints = [
      { id: "", color: selectedPointsDefault[0].color },
      {
        id: response.data.idChart1,
        color: response.data.colorChart1 ?? selectedPointsDefault[1].color,
      },
      {
        id: response.data.idChart2,
        color: response.data.colorChart2 ?? selectedPointsDefault[2].color,
      },
      {
        id: response.data.idChart3,
        color: response.data.colorChart3 ?? selectedPointsDefault[3].color,
      },
      {
        id: response.data.idChart4,
        color: response.data.colorChart4 ?? selectedPointsDefault[4].color,
      },
      {
        id: response.data.idChart5,
        color: response.data.colorChart5 ?? selectedPointsDefault[5].color,
      },
      {
        id: response.data.idChart6,
        color: response.data.colorChart6 ?? selectedPointsDefault[6].color,
      },
      {
        id: response.data.idChart7,
        color: response.data.colorChart7 ?? selectedPointsDefault[7].color,
      },
      {
        id: response.data.idChart8,
        color: response.data.colorChart8 ?? selectedPointsDefault[8].color,
      },
      {
        id: response.data.idChart9,
        color: response.data.colorChart9 ?? selectedPointsDefault[9].color,
      },
      {
        id: response.data.idChart10,
        color: response.data.colorChart10 ?? selectedPointsDefault[10].color,
      },
    ];

    // Aktualisiere den State mit den neu ermittelten Punkten
    setSelectedPoints(selectedPoints);
  } catch (error) {
    console.error("Fehler beim Abrufen der gespeicherten Charts:", error);
  }
};

//========================================================================
// gespeicherte Charts löschen
const deleteSavedCharts = async (id, setSavedCharts) => {
  try {
    await axios
      .post(histDataUrl + "deleteCharts", {
        chartName: id,
      })
      .then(() => getSavedChartsList(setSavedCharts));
  } catch (error) {
    console.error("Fehler beim Löschen der gespeicherten Charts:", error);
  }
};

//========================================================================
// Komponente Trend
const Trend = (props) => {
  const [datasets, setDatasets] = useState([]);
  const [datasetsBool, setDatasetsBool] = useState([]);
  const [historicPoints, setHistoricPoints] = useState([]);
  const [savedCharts, setSavedCharts] = useState([]);
  const [selectedPoints, setSelectedPoints] = useState([
    { id: "", color: "" },
    { id: "", color: "#ED1212" },
    { id: "", color: "#36A2EB" },
    { id: "", color: "#FFCE56" },
    { id: "", color: "#4BC0C0" },
    { id: "", color: "#9966FF" },
    { id: "", color: "#ED1212" },
    { id: "", color: "#36A2EB" },
    { id: "", color: "#FFCE56" },
    { id: "", color: "#4BC0C0" },
    { id: "", color: "#9966FF" },
  ]);
  const [startDate, setStartDate] = useState(
    new Date(new Date().getTime() - 2 * 60 * 60 * 1000),
  );
  const [endDate, setEndDate] = useState(new Date());
  selectedPoints[0].color = "#ff0000";
  const [saveName, setSaveName] = useState("");
  const [selectedId, setSelectedId] = useState();
  const [isZoomEnabled, setIsZoomEnabled] = useState(false); // Neuer State für Zoom

  // Ref für die aktuelle X-Skala
  const xRef = useRef();

  //========================================================================
  // Sprachumschaltung Datepicker
  const localeMap = {
    de: de,
    en: enUS,
    it: it,
  };
  const currentLocale = localeMap[globalVars.language] || de;

  //========================================================================
  // Zeitauswahl begrenzen
  useEffect(() => {
    if (startDate > endDate) {
      setStartDate(endDate);
    }

    // maximal aktuelle Zeit
    if (endDate > new Date()) {
      setEndDate(new Date());
    }
  }, [startDate, endDate]);

  //========================================================================
  // Daten laden
  useEffect(() => {
    let idNum = [];
    let idBool = [];
    for (let i = 1; i <= 5; i++) {
      idNum.push(selectedPoints[i].id);
    }
    for (let i = 6; i <= 10; i++) {
      idBool.push(selectedPoints[i].id);
    }

    fetchData(idNum, startDate, endDate, 0, setDatasets);
    fetchData(idBool, startDate, endDate, 1, setDatasetsBool);
  }, [selectedPoints, startDate, endDate]);

  // wenn alle IDs leer sind, Diagramm löschen
  useEffect(() => {
    if (selectedPoints.every((point) => point.id === "")) {
      setDatasets([]);
      setDatasetsBool([]);
      d3.select("#chart").selectAll("*").remove();
    }
  }, [selectedPoints]);

  //========================================================================
  // Liste aller aufgezeichneten Datenpunkte + gespeicherte Charts laden
  useEffect(() => {
    fetchHistoricPoints(setHistoricPoints);
    getSavedChartsList(setSavedCharts);
  }, []);

  //========================================================================
  // d3-Chart zeichnen, wenn Daten vorliegen oder Zoom aktiviert/deaktiviert wird
  useEffect(() => {
    if (datasets.length > 0 || datasetsBool.length > 0) {
      drawChart();
      // console.log("data: ", datasetsBool);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasets, datasetsBool, globalVars.pageSize, isZoomEnabled]);

  //========================================================================
  // Download
  const buildDownloadData = () => {
    const allDatasets = [...datasets, ...datasetsBool].filter(
      (ds) => ds.data.length > 0,
    );

    if (allDatasets.length === 0) return { headers: [], rows: [] };

    // Spaltennamen pro Dataset (je 2 Spalten: Zeitstempel + Wert)
    const headers = allDatasets.flatMap((ds) => {
      const name = Array.isArray(ds.info.name)
        ? ds.info.name.find((n) => n && n.trim() !== "") || ds.info.id
        : ds.info.name || ds.info.id;
      const unit = ds.info.unit ? ` [${ds.info.unit}]` : "";
      return [`${name} - Zeitstempel`, `${name}${unit}`];
    });

    // Maximale Anzahl Datenpunkte über alle Datasets
    const maxRows = Math.max(...allDatasets.map((ds) => ds.data.length));

    // Zeilen aufbauen: pro Zeile Index i → Wert aus jedem Dataset an Position i
    const rows = Array.from({ length: maxRows }, (_, i) =>
      allDatasets.flatMap((ds) => {
        const point = ds.data[i];
        if (!point) return ["", ""];
        const decimals =
          typeof ds.info.decimalPoints === "number" ? ds.info.decimalPoints : 0;
        return [
          new Date(point.timestamp).toLocaleString(),
          typeof point.value === "number"
            ? point.value.toFixed(decimals)
            : point.value,
        ];
      }),
    );

    return { headers, rows };
  };

  const downloadCSV = () => {
    try {
      const { headers, rows } = buildDownloadData();
      if (headers.length === 0) return;

      const bom = "\uFEFF";
      const lines = [headers, ...rows]
        .map((r) =>
          r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(";"),
        )
        .join("\n");

      const blob = new Blob([bom + lines], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = "Trend.csv";

      document.body.appendChild(a);
      a.click();

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);
    } catch (err) {
      console.error("CSV Download Fehler:", err);
    }
  };

  const downloadXLSX = () => {
    try {
      const { headers, rows } = buildDownloadData();
      if (headers.length === 0) return;

      const wsData = [headers, ...rows];
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Trend");
      XLSX.writeFile(wb, "Trend.xlsx");
    } catch (err) {
      console.error("Excel Download Fehler:", err);
    }
  };

  //========================================================================
  // Funktion zum Zeichnen des Charts
  const drawChart = () => {
    // alten Inhalt entfernen
    d3.select("#chart").selectAll("*").remove();

    const svg = d3
      .select("#chart")
      .append("svg")
      .attr("width", 1900 * globalVars.scaleFactor)
      .attr("height", 775 * globalVars.scaleFactor);

    const margin = {
      top: 101 * globalVars.scaleFactor,
      left: 250 * globalVars.scaleFactor,
    };
    const width = 1622 * globalVars.scaleFactor;
    const height = 606 * globalVars.scaleFactor;

    // Clip Path definieren
    svg
      .append("defs")
      .append("clipPath")
      .attr("id", "clip")
      .append("rect")
      .attr("x", 0)
      .attr("y", -90 * globalVars.scaleFactor) // Verschiebung nach oben
      .attr("width", width)
      .attr("height", height + 80 * globalVars.scaleFactor); // Erhöhung der Höhe

    // Hauptgruppe
    const g = svg
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // X-Skala
    const x = d3.scaleTime().domain([startDate, endDate]).range([0, width]);
    xRef.current = x; // Initiale X-Skala speichern

    // X-Achse
    const xAxis = g
      .append("g")
      .attr("transform", `translate(0,${height})`)
      .call(
        d3
          .axisBottom(x)
          .tickValues([startDate, ...x.ticks(5), endDate])
          .tickFormat(d3.timeFormat("%Y-%m-%d %H:%M:%S")),
      )
      .attr("color", "white");

    xAxis
      .selectAll("text")
      .style("fill", "white")
      .style("text-anchor", "end")
      .attr("dx", "-0.8em")
      .attr("dy", "0.15em")
      .attr("transform", "rotate(-25)");

    //========================================================================
    // Numerische Achsen pro Einheit gruppieren
    const groupedByUnit = d3.group(datasets, (d) => d.info.unit);
    let yAxisOffset = 0;
    const yScales = new Map();

    // Für jede Einheit eine Y-Achse
    groupedByUnit.forEach((datasetsWithUnit, unit) => {
      const allValues = datasetsWithUnit.flatMap((d) =>
        d.data.map((point) => point.value),
      );
      if ((!unit || unit === "") && allValues.length === 0) return;

      const maxVal = d3.max(allValues) || 0;
      const y = d3.scaleLinear().domain([0, maxVal]).range([height, 0]);
      yScales.set(unit, y);

      const axisGroup = g
        .append("g")
        .attr("transform", `translate(${-yAxisOffset},0)`)
        .call(d3.axisLeft(y))
        .attr("color", "white");

      axisGroup.selectAll("text").style("fill", "white");
      axisGroup.selectAll("path").style("stroke", "white");
      axisGroup.selectAll("line").style("stroke", "white");

      // Label mit Einheit
      axisGroup
        .append("text")
        .attr("transform", "rotate(-90)")
        .attr("y", -10)
        .attr("x", -height / 2)
        .attr("dy", "-1em")
        .attr("fill", "white")
        .attr("text-anchor", "middle")
        .text(unit)
        .attr("font-size", "16px");

      // etwas Platz für die nächste Achse
      yAxisOffset += 50 * globalVars.scaleFactor;
    });

    //========================================================================
    // Gruppe für Linien mit Clip Path
    const linesGroup = g.append("g").attr("clip-path", "url(#clip)"); // Clip Path anwenden

    // Gruppe für boolesche Daten mit Clip Path
    const boolGroup = g.append("g").attr("clip-path", "url(#clip)"); // Clip Path anwenden

    // Liniendarstellung (numerische Daten)
    datasets.forEach((dataset, index) => {
      const y = yScales.get(dataset.info.unit);
      if (!y) return;

      const line = d3
        .line()
        .x((d) => x(new Date(d.timestamp)))
        .y((d) => y(d.value));

      const color = selectedPoints[index + 1]?.color || "#000";

      linesGroup
        .append("path")
        .datum(dataset.data)
        .attr("fill", "none")
        .attr("stroke", color)
        .attr("stroke-width", 1.5)
        .attr("d", line)
        .attr("class", `dataset-${index}`); // Klassennamen hinzugefügt
    });

    //========================================================================
    // Tooltip für numerische & boolesche Daten
    //========================================================================
    const tooltip = d3
      .select("#chart")
      .append("div")
      .style("position", "absolute")
      .style("background", "#1a1f2b")
      .style("border", "1px solid #2a3448")
      .style("padding", "8px")
      .style("color", "#cbd5e1")
      .style("border-radius", "4px")
      .style("pointer-events", "none")
      .style("z-index", "1000")
      .style("opacity", 0);

    //========================================================================
    // Tooltip für numerische Daten
    //========================================================================
    datasets.forEach((dataset, index) => {
      const y = yScales.get(dataset.info.unit);
      if (!y) return;

      // Unsichtbarer Pfad als "Hoverzone"
      const line = d3
        .line()
        .x((d) => x(new Date(d.timestamp)))
        .y((d) => y(d.value));

      linesGroup
        .append("path")
        .datum(dataset.data)
        .attr("fill", "none")
        .attr("stroke", "transparent")
        .attr("stroke-width", 10)
        .attr("d", line)
        .attr("class", `hover-dataset-${index}`) // Klassennamen hinzugefügt
        .on("mousemove", (event) => {
          const [xPos] = d3.pointer(event);
          const date = xRef.current.invert(xPos); // Verwendung der aktuellen X-Skala

          const closestPoint = dataset.data.reduce((a, b) =>
            Math.abs(new Date(a.timestamp) - date) <
            Math.abs(new Date(b.timestamp) - date)
              ? a
              : b,
          );

          if (closestPoint) {
            tooltip.html(
              `<strong>${language.displayText(dataset.info.name)}</strong><br/>
               Wert: ${closestPoint.value.toFixed(
                 dataset.info.decimalPoints,
               )} ${dataset.info.unit}<br/>
               Zeit: ${new Date(closestPoint.timestamp).toLocaleString()}`,
            );

            const tooltipWidth = tooltip.node().offsetWidth;
            const tooltipHeight = tooltip.node().offsetHeight;

            // Abstände skalieren
            let leftPos = event.pageX - 200 * globalVars.scaleFactor;
            let topPos = event.pageY + 10 * globalVars.scaleFactor;

            // Ränder checken
            if (
              leftPos + tooltipWidth >
              parseFloat(globalVars.pageSize.width)
            ) {
              leftPos =
                event.pageX - tooltipWidth - 200 * globalVars.scaleFactor;
            }
            if (topPos + tooltipHeight > window.innerHeight) {
              topPos =
                event.pageY - tooltipHeight - 10 * globalVars.scaleFactor;
            }

            tooltip
              .style("opacity", 1)
              .style("left", `${leftPos}px`)
              .style("top", `${topPos}px`);
          }
        })
        .on("mouseleave", () => {
          tooltip.style("opacity", 0);
        });
    });

    //========================================================================
    // Boolesche Achsen + (step)Flächen
    //========================================================================
    let boolBaseOffset = -685 * globalVars.scaleFactor;
    datasetsBool.forEach((datasetBoolItem, idx) => {
      const color = selectedPoints[5 + idx + 1]?.color || "#000";
      const yBool = d3
        .scaleLinear()
        .domain([0, 1])
        .range([0, -10 * globalVars.scaleFactor]);

      // Step-Fläche (Area) mit 90° Sprung
      const boolArea = d3
        .area()
        .x((d) => x(new Date(d.timestamp)))
        .y0(
          height +
            boolBaseOffset +
            idx * (15 * globalVars.scaleFactor) +
            yBool(0),
        )
        .y1(
          (d) =>
            height +
            boolBaseOffset +
            idx * (15 * globalVars.scaleFactor) +
            yBool(d.value ?? 0),
        )
        .curve(d3.curveStepAfter);

      // Zeichne "Achse" in passender Farbe
      const axisGroupBool = g
        .append("g")
        .attr(
          "transform",
          `translate(0,${
            height + boolBaseOffset + idx * (15 * globalVars.scaleFactor)
          })`,
        )
        .call(d3.axisLeft(yBool).tickValues([]).tickSize(0))
        .attr("color", color);

      axisGroupBool.selectAll("path").style("stroke", color);
      axisGroupBool.selectAll("line").style("stroke", color);
      axisGroupBool.selectAll("text").remove();

      // Gefüllte Fläche innerhalb des boolGroup
      boolGroup
        .append("path")
        .datum(datasetBoolItem.data)
        .attr("fill", color)
        .attr("fill-opacity", 0.7)
        .attr("stroke", color)
        .attr("stroke-width", 1.5 * globalVars.scaleFactor)
        .attr("d", boolArea)
        .attr("class", `bool-${idx}`) // Klassennamen hinzugefügt
        // Tooltip auch für Bool-Daten
        .on("mousemove", (event) => {
          const [xPos] = d3.pointer(event);
          const date = xRef.current.invert(xPos); // Verwendung der aktuellen X-Skala

          // Nächsten Punkt bestimmen
          const closestPoint = datasetBoolItem.data.reduce((a, b) =>
            Math.abs(new Date(a.timestamp) - date) <
            Math.abs(new Date(b.timestamp) - date)
              ? a
              : b,
          );

          if (closestPoint) {
            const boolVal = closestPoint.value ? 1 : 0;

            tooltip.html(
              `<strong>${language.displayText(
                datasetBoolItem.info.name,
              )}</strong><br/>
               Wert: ${boolVal}<br/>
               Zeit: ${new Date(closestPoint.timestamp).toLocaleString()}`,
            );

            const tooltipWidth = tooltip.node().offsetWidth;
            const tooltipHeight = tooltip.node().offsetHeight;

            let leftPos = event.pageX - 200 * globalVars.scaleFactor;
            let topPos = event.pageY + 20 * globalVars.scaleFactor;

            // Ränder checken
            if (
              leftPos + tooltipWidth >
              parseFloat(globalVars.pageSize.width)
            ) {
              leftPos =
                event.pageX - tooltipWidth - 200 * globalVars.scaleFactor;
            }
            if (topPos + tooltipHeight > window.innerHeight) {
              topPos =
                event.pageY - tooltipHeight - 10 * globalVars.scaleFactor;
            }

            tooltip
              .style("opacity", 1)
              .style("left", `${leftPos}px`)
              .style("top", `${topPos}px`);
          }
        })
        .on("mouseleave", () => {
          tooltip.style("opacity", 0);
        });
    });

    //========================================================================
    // Horizontalen Zoom hinzufügen, wenn Zoom aktiviert ist
    if (isZoomEnabled) {
      const zoom = d3
        .zoom()
        .scaleExtent([1, 10])
        .translateExtent([
          [0, 0],
          [width, height],
        ])
        .extent([
          [0, 0],
          [width, height],
        ])
        .on("zoom", zoomed);

      svg.call(zoom);

      function zoomed(event) {
        const newX = event.transform.rescaleX(x);
        xRef.current = newX; // Aktualisiere die Referenz zur aktuellen X-Skala

        xAxis.call(
          d3
            .axisBottom(newX)
            .tickValues([startDate, ...newX.ticks(5), endDate])
            .tickFormat(d3.timeFormat("%Y-%m-%d %H:%M:%S")),
        );

        xAxis
          .selectAll("text")
          .style("fill", "white")
          .style("text-anchor", "end")
          .attr("dx", "-0.8em")
          .attr("dy", "0.15em")
          .attr("transform", "rotate(-25)");

        // Aktualisiere die Linien für numerische Daten
        datasets.forEach((dataset, index) => {
          const y = yScales.get(dataset.info.unit);
          if (!y) return;

          const line = d3
            .line()
            .x((d) => newX(new Date(d.timestamp)))
            .y((d) => y(d.value));

          linesGroup
            .selectAll(`path.dataset-${index}`)
            .attr("d", line(dataset.data));
        });

        // Aktualisiere die Hover-Zonen für numerische Daten
        datasets.forEach((dataset, index) => {
          const y = yScales.get(dataset.info.unit);
          if (!y) return;

          const line = d3
            .line()
            .x((d) => newX(new Date(d.timestamp)))
            .y((d) => y(d.value));

          linesGroup
            .selectAll(`path.hover-dataset-${index}`)
            .attr("d", line(dataset.data));
        });

        // Aktualisiere die Flächen für boolesche Daten
        datasetsBool.forEach((datasetBoolItem, idx) => {
          const color = selectedPoints[5 + idx + 1]?.color || "#000";
          const yBool = d3
            .scaleLinear()
            .domain([0, 1])
            .range([0, -10 * globalVars.scaleFactor]);

          const boolArea = d3
            .area()
            .x((d) => newX(new Date(d.timestamp)))
            .y0(
              height +
                boolBaseOffset +
                idx * (15 * globalVars.scaleFactor) +
                yBool(0),
            )
            .y1(
              (d) =>
                height +
                boolBaseOffset +
                idx * (15 * globalVars.scaleFactor) +
                yBool(d.value ?? 0),
            )
            .curve(d3.curveStepAfter);

          boolGroup
            .selectAll(`path.bool-${idx}`)
            .attr("d", boolArea(datasetBoolItem.data));
        });
      }
    }

    //========================================================================
    // Zoom Reset, wenn Zoom deaktiviert wird
    //========================================================================
    if (!isZoomEnabled) {
      svg
        .transition()
        .duration(750)
        .call(
          d3.zoom().transform,
          d3.zoomIdentity,
          d3.zoomTransform(svg.node()).invert([width / 2, height / 2]),
        );
      xRef.current = d3
        .scaleTime()
        .domain([startDate, endDate])
        .range([0, width]);

      // Aktualisiere die Achsen
      xAxis.call(
        d3
          .axisBottom(xRef.current)
          .tickValues([startDate, ...xRef.current.ticks(5), endDate])
          .tickFormat(d3.timeFormat("%Y-%m-%d %H:%M:%S")),
      );

      xAxis
        .selectAll("text")
        .style("fill", "white")
        .style("text-anchor", "end")
        .attr("dx", "-0.8em")
        .attr("dy", "0.15em")
        .attr("transform", "rotate(-25)");

      // Aktualisiere die Linien für numerische Daten
      datasets.forEach((dataset, index) => {
        const y = yScales.get(dataset.info.unit);
        if (!y) return;

        const line = d3
          .line()
          .x((d) => xRef.current(new Date(d.timestamp)))
          .y((d) => y(d.value));

        linesGroup
          .selectAll(`path.dataset-${index}`)
          .attr("d", line(dataset.data));
      });

      // Aktualisiere die Hover-Zonen für numerische Daten
      datasets.forEach((dataset, index) => {
        const y = yScales.get(dataset.info.unit);
        if (!y) return;

        const line = d3
          .line()
          .x((d) => xRef.current(new Date(d.timestamp)))
          .y((d) => y(d.value));

        linesGroup
          .selectAll(`path.hover-dataset-${index}`)
          .attr("d", line(dataset.data));
      });

      // Aktualisiere die Flächen für boolesche Daten
      datasetsBool.forEach((datasetBoolItem, idx) => {
        const color = selectedPoints[5 + idx + 1]?.color || "#000";
        const yBool = d3
          .scaleLinear()
          .domain([0, 1])
          .range([0, -10 * globalVars.scaleFactor]);

        const boolArea = d3
          .area()
          .x((d) => xRef.current(new Date(d.timestamp)))
          .y0(
            height +
              boolBaseOffset +
              idx * (15 * globalVars.scaleFactor) +
              yBool(0),
          )
          .y1(
            (d) =>
              height +
              boolBaseOffset +
              idx * (15 * globalVars.scaleFactor) +
              yBool(d.value ?? 0),
          )
          .curve(d3.curveStepAfter);

        boolGroup
          .selectAll(`path.bool-${idx}`)
          .attr("d", boolArea(datasetBoolItem.data));
      });
    }
  };

  //========================================================================
  // Grafische Darstellung
  return (
    <div>
      {/*************** grauer Hintergrund ***************/}
      <div
        style={{
          position: "absolute",
          top: render.parseCssValue("0px", globalVars.scaleFactor),
          left: render.parseCssValue("0px", globalVars.scaleFactor),
          height: render.parseCssValue("1010px", globalVars.scaleFactor),
          width: render.parseCssValue("1920px", globalVars.scaleFactor),
          backgroundColor: "#0d1018",
        }}
      ></div>
      {/*************** dunkelgrauer Hintergrund Diagramm ***************/}
      <div
        style={{
          position: "absolute",
          top: render.parseCssValue("10px", globalVars.scaleFactor),
          left: render.parseCssValue("10px", globalVars.scaleFactor),
          height: render.parseCssValue("775px", globalVars.scaleFactor),
          width: render.parseCssValue("1900px", globalVars.scaleFactor),
          backgroundColor: "#0d1018",
        }}
      ></div>
      {/*************** schwarzer Hintergrund Diagramm bool ***************/}
      <div
        style={{
          position: "absolute",
          top: render.parseCssValue("15px", globalVars.scaleFactor),
          left: render.parseCssValue("270px", globalVars.scaleFactor),
          height: render.parseCssValue("85px", globalVars.scaleFactor),
          width: render.parseCssValue("1622px", globalVars.scaleFactor),
          backgroundColor: "black",
          border: "1px solid #2a3448",
        }}
      ></div>
      {/*************** x-Achse bool Datenpunkt 1 ***************/}
      <div
        style={{
          position: "absolute",
          top: render.parseCssValue("31px", globalVars.scaleFactor),
          left: render.parseCssValue("270px", globalVars.scaleFactor),
          height: render.parseCssValue("2px", globalVars.scaleFactor),
          width: render.parseCssValue("1623px", globalVars.scaleFactor),
          backgroundColor: selectedPoints[6].color,
        }}
      ></div>
      {/*************** x-Achse bool Datenpunkt 2 ***************/}
      <div
        style={{
          position: "absolute",
          top: render.parseCssValue("46px", globalVars.scaleFactor),
          left: render.parseCssValue("270px", globalVars.scaleFactor),
          height: render.parseCssValue("2px", globalVars.scaleFactor),
          width: render.parseCssValue("1623px", globalVars.scaleFactor),
          backgroundColor: selectedPoints[7].color,
        }}
      ></div>
      {/*************** x-Achse bool Datenpunkt 3 ***************/}
      <div
        style={{
          position: "absolute",
          top: render.parseCssValue("61px", globalVars.scaleFactor),
          left: render.parseCssValue("270px", globalVars.scaleFactor),
          height: render.parseCssValue("2px", globalVars.scaleFactor),
          width: render.parseCssValue("1623px", globalVars.scaleFactor),
          backgroundColor: selectedPoints[8].color,
        }}
      ></div>
      {/*************** x-Achse bool Datenpunkt 4 ***************/}
      <div
        style={{
          position: "absolute",
          top: render.parseCssValue("76px", globalVars.scaleFactor),
          left: render.parseCssValue("270px", globalVars.scaleFactor),
          height: render.parseCssValue("2px", globalVars.scaleFactor),
          width: render.parseCssValue("1623px", globalVars.scaleFactor),
          backgroundColor: selectedPoints[9].color,
        }}
      ></div>
      {/*************** x-Achse bool Datenpunkt 5 ***************/}
      <div
        style={{
          position: "absolute",
          top: render.parseCssValue("91px", globalVars.scaleFactor),
          left: render.parseCssValue("270px", globalVars.scaleFactor),
          height: render.parseCssValue("2px", globalVars.scaleFactor),
          width: render.parseCssValue("1623px", globalVars.scaleFactor),
          backgroundColor: selectedPoints[10].color,
        }}
      ></div>
      {/*************** schwarzer Hintergrund Diagramm numerisch ***************/}
      <div
        style={{
          position: "absolute",
          top: render.parseCssValue("106px", globalVars.scaleFactor),
          left: render.parseCssValue("270px", globalVars.scaleFactor),
          height: render.parseCssValue("610px", globalVars.scaleFactor),
          width: render.parseCssValue("1622px", globalVars.scaleFactor),
          backgroundColor: "black",
          border: "1px solid #2a3448",
        }}
      ></div>
      {/*************** grauer Hintergrund Auswahl ***************/}
      <div
        style={{
          position: "absolute",
          top: render.parseCssValue("795px", globalVars.scaleFactor),
          left: render.parseCssValue("10px", globalVars.scaleFactor),
          height: render.parseCssValue("205px", globalVars.scaleFactor),
          width: render.parseCssValue("1900px", globalVars.scaleFactor),
          backgroundColor: "#161b26",
        }}
      ></div>
      {/*************** leere y-Achse ***************/}
      <div
        style={{
          position: "absolute",
          top: render.parseCssValue("107px", globalVars.scaleFactor),
          left: render.parseCssValue("270px", globalVars.scaleFactor),
          height: render.parseCssValue("616px", globalVars.scaleFactor),
          width: render.parseCssValue("1px", globalVars.scaleFactor),
          backgroundColor: "#474a52",
        }}
      ></div>
      {/*************** Diagramm ***************/}
      <div
        style={{
          position: "absolute",
          top: render.parseCssValue("10px", globalVars.scaleFactor),
          left: render.parseCssValue("20px", globalVars.scaleFactor),
          height: render.parseCssValue("775px", globalVars.scaleFactor),
          width: render.parseCssValue("1880px", globalVars.scaleFactor),
        }}
        id="chart"
      ></div>
      {/*************** Auswahl Datenpunkte ***************/}
      <DataAndColorPicker
        index={1}
        style={{
          top: render.parseCssValue("805px", globalVars.scaleFactor),
          left: render.parseCssValue("20px", globalVars.scaleFactor),
          height: render.parseCssValue("30px", globalVars.scaleFactor),
          width: render.parseCssValue("600px", globalVars.scaleFactor),
          fontSize: render.parseCssValue("16px", globalVars.scaleFactor),
        }}
        historicPoints={historicPoints}
        selectedPoints={selectedPoints}
        setSelectedPoints={setSelectedPoints}
        type={"num"}
      />
      <DataAndColorPicker
        index={2}
        style={{
          top: render.parseCssValue("845px", globalVars.scaleFactor),
          left: render.parseCssValue("20px", globalVars.scaleFactor),
          height: render.parseCssValue("30px", globalVars.scaleFactor),
          width: render.parseCssValue("600px", globalVars.scaleFactor),
          fontSize: render.parseCssValue("16px", globalVars.scaleFactor),
        }}
        historicPoints={historicPoints}
        selectedPoints={selectedPoints}
        setSelectedPoints={setSelectedPoints}
        type={"num"}
      />
      <DataAndColorPicker
        index={3}
        style={{
          top: render.parseCssValue("885px", globalVars.scaleFactor),
          left: render.parseCssValue("20px", globalVars.scaleFactor),
          height: render.parseCssValue("30px", globalVars.scaleFactor),
          width: render.parseCssValue("600px", globalVars.scaleFactor),
          fontSize: render.parseCssValue("16px", globalVars.scaleFactor),
        }}
        historicPoints={historicPoints}
        selectedPoints={selectedPoints}
        setSelectedPoints={setSelectedPoints}
        type={"num"}
      />
      <DataAndColorPicker
        index={4}
        style={{
          top: render.parseCssValue("925px", globalVars.scaleFactor),
          left: render.parseCssValue("20px", globalVars.scaleFactor),
          height: render.parseCssValue("30px", globalVars.scaleFactor),
          width: render.parseCssValue("600px", globalVars.scaleFactor),
          fontSize: render.parseCssValue("16px", globalVars.scaleFactor),
        }}
        historicPoints={historicPoints}
        selectedPoints={selectedPoints}
        setSelectedPoints={setSelectedPoints}
        type={"num"}
      />
      <DataAndColorPicker
        index={5}
        style={{
          top: render.parseCssValue("965px", globalVars.scaleFactor),
          left: render.parseCssValue("20px", globalVars.scaleFactor),
          height: render.parseCssValue("30px", globalVars.scaleFactor),
          width: render.parseCssValue("600px", globalVars.scaleFactor),
          fontSize: render.parseCssValue("16px", globalVars.scaleFactor),
        }}
        historicPoints={historicPoints}
        selectedPoints={selectedPoints}
        setSelectedPoints={setSelectedPoints}
        type={"num"}
      />
      {/*************** Auswahl Datenpunkte Bool ***************/}
      <DataAndColorPicker
        index={6}
        style={{
          top: render.parseCssValue("805px", globalVars.scaleFactor),
          left: render.parseCssValue("650px", globalVars.scaleFactor),
          height: render.parseCssValue("30px", globalVars.scaleFactor),
          width: render.parseCssValue("600px", globalVars.scaleFactor),
          fontSize: render.parseCssValue("16px", globalVars.scaleFactor),
        }}
        historicPoints={historicPoints}
        selectedPoints={selectedPoints}
        setSelectedPoints={setSelectedPoints}
        type={"bool"}
      />
      <DataAndColorPicker
        index={7}
        style={{
          top: render.parseCssValue("845px", globalVars.scaleFactor),
          left: render.parseCssValue("650px", globalVars.scaleFactor),
          height: render.parseCssValue("30px", globalVars.scaleFactor),
          width: render.parseCssValue("600px", globalVars.scaleFactor),
          fontSize: render.parseCssValue("16px", globalVars.scaleFactor),
        }}
        historicPoints={historicPoints}
        selectedPoints={selectedPoints}
        setSelectedPoints={setSelectedPoints}
        type={"bool"}
      />
      <DataAndColorPicker
        index={8}
        style={{
          top: render.parseCssValue("885px", globalVars.scaleFactor),
          left: render.parseCssValue("650px", globalVars.scaleFactor),
          height: render.parseCssValue("30px", globalVars.scaleFactor),
          width: render.parseCssValue("600px", globalVars.scaleFactor),
          fontSize: render.parseCssValue("16px", globalVars.scaleFactor),
        }}
        historicPoints={historicPoints}
        selectedPoints={selectedPoints}
        setSelectedPoints={setSelectedPoints}
        type={"bool"}
      />
      <DataAndColorPicker
        index={9}
        style={{
          top: render.parseCssValue("925px", globalVars.scaleFactor),
          left: render.parseCssValue("650px", globalVars.scaleFactor),
          height: render.parseCssValue("30px", globalVars.scaleFactor),
          width: render.parseCssValue("600px", globalVars.scaleFactor),
          fontSize: render.parseCssValue("16px", globalVars.scaleFactor),
        }}
        historicPoints={historicPoints}
        selectedPoints={selectedPoints}
        setSelectedPoints={setSelectedPoints}
        type={"bool"}
      />
      <DataAndColorPicker
        index={10}
        style={{
          top: render.parseCssValue("965px", globalVars.scaleFactor),
          left: render.parseCssValue("650px", globalVars.scaleFactor),
          height: render.parseCssValue("30px", globalVars.scaleFactor),
          width: render.parseCssValue("600px", globalVars.scaleFactor),
          fontSize: render.parseCssValue("16px", globalVars.scaleFactor),
        }}
        historicPoints={historicPoints}
        selectedPoints={selectedPoints}
        setSelectedPoints={setSelectedPoints}
        type={"bool"}
      />
      {/*************** Hintergrund Zeitauswahl ***************/}
      <div
        style={{
          position: "absolute",
          top: render.parseCssValue("815px", globalVars.scaleFactor),
          left: render.parseCssValue("1290px", globalVars.scaleFactor),
          height: render.parseCssValue("175px", globalVars.scaleFactor),
          width: render.parseCssValue("200px", globalVars.scaleFactor),
          backgroundColor: "#0d1018",
        }}
      ></div>
      {/*************** Label Startzeit ***************/}
      <div
        style={{
          position: "absolute",
          top: render.parseCssValue("825px", globalVars.scaleFactor),
          left: render.parseCssValue("1300px", globalVars.scaleFactor),
          height: render.parseCssValue("25px", globalVars.scaleFactor),
          width: render.parseCssValue("180px", globalVars.scaleFactor),
          fontSize: render.parseCssValue("16px", globalVars.scaleFactor),
          color: "#a9b1bd",
          textAlign: "center",
        }}
      >
        {language.displayText(["Startzeit", "inizio", "starttime"])}
      </div>
      {/*************** Auswahl Startzeit ***************/}
      <div
        style={{
          position: "absolute",
          top: render.parseCssValue("850px", globalVars.scaleFactor),
          left: render.parseCssValue("1300px", globalVars.scaleFactor),
          height: render.parseCssValue("30px", globalVars.scaleFactor),
          width: render.parseCssValue("500px", globalVars.scaleFactor),
          fontSize: render.parseCssValue("16px", globalVars.scaleFactor),
        }}
      >
        <DatePicker
          selected={startDate}
          onChange={(date) => setStartDate(date)}
          showTimeSelect
          timeIntervals={15}
          timeCaption={language.displayText(["Zeit", "tempo", "time"])}
          timeFormat="HH:mm"
          dateFormat="dd.MM.yyyy HH:mm"
          locale={currentLocale}
          customInput={
            <input
              style={{
                top: render.parseCssValue("0px", globalVars.scaleFactor),
                left: render.parseCssValue("10px", globalVars.scaleFactor),
                height: render.parseCssValue("20px", globalVars.scaleFactor),
                width: render.parseCssValue("170px", globalVars.scaleFactor),
                textAlign: "center",
                background: "#0f1117",
                border: "1px solid #2a3448",
                borderRadius: "6px",
                color: "#cbd5e1",
                fontFamily: "'IBM Plex Mono', monospace",
                outline: "none",
                cursor: "pointer",
              }}
            />
          }
        />
      </div>
      {/*************** Label Endzeit ***************/}
      <div
        style={{
          position: "absolute",
          top: render.parseCssValue("885px", globalVars.scaleFactor),
          left: render.parseCssValue("1300px", globalVars.scaleFactor),
          height: render.parseCssValue("25px", globalVars.scaleFactor),
          width: render.parseCssValue("180px", globalVars.scaleFactor),
          fontSize: render.parseCssValue("16px", globalVars.scaleFactor),
          color: "#a9b1bd",
          textAlign: "center",
        }}
      >
        {language.displayText(["Endzeit", "fine", "endtime"])}
      </div>
      {/*************** Auswahl Endzeit ***************/}
      <div
        style={{
          position: "absolute",
          top: render.parseCssValue("910px", globalVars.scaleFactor),
          left: render.parseCssValue("1300px", globalVars.scaleFactor),
          height: render.parseCssValue("30px", globalVars.scaleFactor),
          width: render.parseCssValue("500px", globalVars.scaleFactor),
          fontSize: render.parseCssValue("16px", globalVars.scaleFactor),
        }}
      >
        <DatePicker
          selected={endDate}
          onChange={(date) => setEndDate(date)}
          showTimeSelect
          timeIntervals={15}
          timeCaption={language.displayText(["Zeit", "tempo", "time"])}
          timeFormat="HH:mm"
          dateFormat="dd.MM.yyyy HH:mm"
          locale={currentLocale}
          customInput={
            <input
              style={{
                top: render.parseCssValue("0px", globalVars.scaleFactor),
                left: render.parseCssValue("10px", globalVars.scaleFactor),
                height: render.parseCssValue("20px", globalVars.scaleFactor),
                width: render.parseCssValue("170px", globalVars.scaleFactor),
                textAlign: "center",
                background: "#0f1117",
                border: "1px solid #2a3448",
                borderRadius: "6px",
                color: "#cbd5e1",
                fontFamily: "'IBM Plex Mono', monospace",
                outline: "none",
                cursor: "pointer",
              }}
            />
          }
        />
      </div>
      {/*************** Button Zoom ***************/}
      <button
        style={{
          position: "absolute",
          top: render.parseCssValue("950px", globalVars.scaleFactor),
          left: render.parseCssValue("1300px", globalVars.scaleFactor),
          height: render.parseCssValue("30px", globalVars.scaleFactor),
          width: render.parseCssValue("180px", globalVars.scaleFactor),
          fontSize: render.parseCssValue("16px", globalVars.scaleFactor),
          backgroundColor: isZoomEnabled ? "#2a6e2a" : "#1e2330",
          border: "1px solid #2a3448",
          borderRadius: "6px",
          color: "#cbd5e1",
          cursor: "pointer",
        }}
        onClick={() => setIsZoomEnabled(!isZoomEnabled)}
      >
        {isZoomEnabled ? "Zoom On" : "Zoom Off"}
      </button>
      {/*************** Hintergrund Gruppen ***************/}
      <div
        style={{
          position: "absolute",
          top: render.parseCssValue("815px", globalVars.scaleFactor),
          left: render.parseCssValue("1525px", globalVars.scaleFactor),
          height: render.parseCssValue("175px", globalVars.scaleFactor),
          width: render.parseCssValue("350px", globalVars.scaleFactor),
          backgroundColor: "#0d1018",
        }}
      ></div>
      {/*************** Label Gruppen ***************/}
      <div
        style={{
          position: "absolute",
          top: render.parseCssValue("825px", globalVars.scaleFactor),
          left: render.parseCssValue("1535px", globalVars.scaleFactor),
          height: render.parseCssValue("25px", globalVars.scaleFactor),
          width: render.parseCssValue("330px", globalVars.scaleFactor),
          fontSize: render.parseCssValue("16px", globalVars.scaleFactor),
          color: "#a9b1bd",
          display: "flex",
          justifyContent: "center",
        }}
      >
        {language.displayText(["Gruppen", "gruppi", "groups"])}
      </div>
      {/*************** Auswahl laden ***************/}
      <select
        style={{
          position: "absolute",
          top: render.parseCssValue("850px", globalVars.scaleFactor),
          left: render.parseCssValue("1535px", globalVars.scaleFactor),
          height: render.parseCssValue("30px", globalVars.scaleFactor),
          width: render.parseCssValue("178px", globalVars.scaleFactor),
          fontSize: render.parseCssValue("16px", globalVars.scaleFactor),
          background: "#0f1117",
          border: "1px solid #2a3448",
          borderRadius: "6px",
          color: "#cbd5e1",
          outline: "none",
          cursor: "pointer",
        }}
        onChange={(event) =>
          getSavedCharts(event, setSelectedPoints, setSelectedId)
        }
        value={selectedId}
      >
        <option value=""></option>
        {savedCharts.map((item) => (
          <option key={item.id} value={item.id}>
            {item}
          </option>
        ))}
      </select>
      {/*************** Button löschen ***************/}
      <button
        style={{
          position: "absolute",
          top: render.parseCssValue("850px", globalVars.scaleFactor),
          left: render.parseCssValue("1715px", globalVars.scaleFactor),
          height: render.parseCssValue("30px", globalVars.scaleFactor),
          width: render.parseCssValue("150px", globalVars.scaleFactor),
          fontSize: render.parseCssValue("16px", globalVars.scaleFactor),
          background: "#1e2330",
          border: "1px solid #2a3448",
          borderRadius: "6px",
          color: "#cbd5e1",
          cursor: "pointer",
        }}
        onClick={() => deleteSavedCharts(selectedId, setSavedCharts)}
      >
        {language.displayText(["Löschen", "cancella", "delete"])}
      </button>
      {/*************** Name speichern ***************/}
      <input
        type="text"
        style={{
          position: "absolute",
          top: render.parseCssValue("900px", globalVars.scaleFactor),
          left: render.parseCssValue("1535px", globalVars.scaleFactor),
          height: render.parseCssValue("23px", globalVars.scaleFactor),
          width: render.parseCssValue("170px", globalVars.scaleFactor),
          fontSize: render.parseCssValue("14px", globalVars.scaleFactor),
          background: "#0f1117",
          border: "1px solid #2a3448",
          borderRadius: "6px",
          color: "#cbd5e1",
          outline: "none",
        }}
        onChange={(event) => setSaveName(event.target.value)}
        value={saveName}
        placeholder={language.displayText([
          "neue Diagrammgruppe",
          "nuovo gruppo",
          "new group",
        ])}
      ></input>
      {/*************** Button speichern ***************/}
      <button
        style={{
          position: "absolute",
          top: render.parseCssValue("900px", globalVars.scaleFactor),
          left: render.parseCssValue("1715px", globalVars.scaleFactor),
          height: render.parseCssValue("30px", globalVars.scaleFactor),
          width: render.parseCssValue("150px", globalVars.scaleFactor),
          fontSize: render.parseCssValue("16px", globalVars.scaleFactor),
          background: "#1e2330",
          border: "1px solid #2a3448",
          borderRadius: "6px",
          color: "#cbd5e1",
          cursor: "pointer",
        }}
        onClick={() =>
          saveChart(saveName, 0, selectedPoints, setSaveName, setSavedCharts)
        }
      >
        {language.displayText(["Speichern", "Salva", "Save"])}
      </button>
      {/*************** Trennlinie Download ***************/}
      <div
        style={{
          position: "absolute",
          top: render.parseCssValue("950px", globalVars.scaleFactor),
          left: render.parseCssValue("1535px", globalVars.scaleFactor),
          width: render.parseCssValue("330px", globalVars.scaleFactor),
          height: render.parseCssValue("1px", globalVars.scaleFactor),
          backgroundColor: "#1e2a3a",
        }}
      ></div>
      {/*************** Button Download CSV ***************/}
      <button
        style={{
          position: "absolute",
          top: render.parseCssValue("950px", globalVars.scaleFactor),
          left: render.parseCssValue("1535px", globalVars.scaleFactor),
          height: render.parseCssValue("30px", globalVars.scaleFactor),
          width: render.parseCssValue("158px", globalVars.scaleFactor),
          fontSize: render.parseCssValue("13px", globalVars.scaleFactor),
          background: "#1e2330",
          border: "1px solid #2a3448",
          borderRadius: "6px",
          color: "#cbd5e1",
          cursor: "pointer",
        }}
        onClick={downloadCSV}
      >
        Download CSV
      </button>
      {/*************** Button Download Excel ***************/}
      <button
        style={{
          position: "absolute",
          top: render.parseCssValue("950px", globalVars.scaleFactor),
          left: render.parseCssValue("1707px", globalVars.scaleFactor),
          height: render.parseCssValue("30px", globalVars.scaleFactor),
          width: render.parseCssValue("158px", globalVars.scaleFactor),
          fontSize: render.parseCssValue("13px", globalVars.scaleFactor),
          background: "#1e2330",
          border: "1px solid #2a3448",
          borderRadius: "6px",
          color: "#cbd5e1",
          cursor: "pointer",
        }}
        onClick={downloadXLSX}
      >
        Download Excel
      </button>
    </div>
  );
};

export default Trend;
